'use client'

import { useState, useEffect, useCallback, useRef, useSyncExternalStore, type RefObject } from 'react'

export interface FullscreenState {
  isFullscreen: boolean
  isSupported: boolean
  enterFullscreen: () => Promise<void>
  exitFullscreen: () => Promise<void>
  toggleFullscreen: () => Promise<void>
}

/**
 * Compatibilidad con las cuatro variantes de la Fullscreen API.
 *
 * Cada operación se resuelve recorriendo su lista de nombres en vez de con una
 * escalera de `if/else if`: el nombre correcto es un dato, no una rama, y así
 * la lógica del hook queda a la vista sin cuatro comprobaciones por función.
 */
const REQUEST_METHODS = [
  'requestFullscreen',
  'webkitRequestFullscreen',
  'mozRequestFullScreen',
  'msRequestFullscreen',
] as const

const EXIT_METHODS = [
  'exitFullscreen',
  'webkitExitFullscreen',
  'mozCancelFullScreen',
  'msExitFullscreen',
] as const

const ELEMENT_PROPERTIES = [
  'fullscreenElement',
  'webkitFullscreenElement',
  'mozFullScreenElement',
  'msFullscreenElement',
] as const

const ENABLED_PROPERTIES = [
  'fullscreenEnabled',
  'webkitFullscreenEnabled',
  'mozFullScreenEnabled',
  'msFullscreenEnabled',
] as const

const CHANGE_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange',
] as const

type VendorRecord = Record<string, unknown>

function firstMethod(host: object, names: readonly string[]): (() => Promise<void>) | null {
  for (const name of names) {
    const candidate = (host as VendorRecord)[name]
    if (typeof candidate === 'function') return candidate.bind(host) as () => Promise<void>
  }
  return null
}

function anyTruthyProperty(host: object, names: readonly string[]): boolean {
  return names.some((name) => Boolean((host as VendorRecord)[name]))
}

function isDocumentFullscreen(): boolean {
  return anyTruthyProperty(document, ELEMENT_PROPERTIES)
}

/**
 * `fullscreenEnabled` no cambia durante la vida de la página, así que se lee
 * como una fuente externa en vez de meterla en estado desde un efecto: así no
 * hay un render extra con `isSupported` en falso, y el servidor devuelve
 * `false` sin tocar `document`.
 */
const SUPPORT_STORE = {
  subscribe: () => () => {},
  getSnapshot: () => anyTruthyProperty(document, ENABLED_PROPERTIES),
  getServerSnapshot: () => false,
}

function logFullscreenFailure(error: unknown): void {
  const name = (error as Error).name
  if (name === 'NotAllowedError') {
    console.log('Fullscreen requires user gesture')
  } else if (name === 'TypeError') {
    console.log('Fullscreen not supported')
  } else {
    console.error('Fullscreen error:', error)
  }
}

/**
 * @param target elemento a poner a pantalla completa. Por defecto el documento
 *   entero, que es lo que quieren las pantallas de llamada; el tracker pasa el
 *   contenedor de su mapa para dejar fuera el resto de la página.
 */
export function useFullscreen(target?: RefObject<HTMLElement | null>): FullscreenState {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isSupported = useSyncExternalStore(
    SUPPORT_STORE.subscribe,
    SUPPORT_STORE.getSnapshot,
    SUPPORT_STORE.getServerSnapshot
  )

  // Espejo del `ref` recibido: así `enterFullscreen` no depende de él y su
  // identidad se mantiene estable entre renders del componente que lo usa.
  const targetRef = useRef(target)
  useEffect(() => {
    targetRef.current = target
  }, [target])

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(isDocumentFullscreen())
    for (const event of CHANGE_EVENTS) {
      document.addEventListener(event, handleFullscreenChange)
    }

    return () => {
      for (const event of CHANGE_EVENTS) {
        document.removeEventListener(event, handleFullscreenChange)
      }
    }
  }, [])

  const enterFullscreen = useCallback(async () => {
    if (!isSupported) {
      console.log('Fullscreen API not supported')
      return
    }

    const element = targetRef.current?.current ?? document.documentElement
    const request = firstMethod(element, REQUEST_METHODS)
    if (!request) return

    try {
      await request()
    } catch (error) {
      logFullscreenFailure(error)
    }
  }, [isSupported])

  const exitFullscreen = useCallback(async () => {
    if (!isSupported) return

    // Salir sin estar en pantalla completa lanza en algunos motores.
    if (!isDocumentFullscreen()) {
      console.log('Not in fullscreen, skipping exit')
      return
    }

    const exit = firstMethod(document, EXIT_METHODS)
    if (!exit) return

    try {
      await exit()
    } catch (error) {
      console.error('Exit fullscreen error:', error)
    }
  }, [isSupported])

  const toggleFullscreen = useCallback(async () => {
    await (isFullscreen ? exitFullscreen() : enterFullscreen())
  }, [isFullscreen, enterFullscreen, exitFullscreen])

  return {
    isFullscreen,
    isSupported,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  }
}
